import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import { CommonFunctionsService } from 'src/app/services/common-functions.service';
import {
  IEvent,
  EventViewMode,
  IComponentChanged,
  DateGroupedWorkPackages,
  User,
  IWorkPackage,
} from 'src/app/services/definitions.service';
import { ThreadSafeService } from 'src/app/services/thread-safe-component-state.service';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'grouped-work-packages',
  templateUrl: './grouped-work-packages.component.html',
  styleUrls: ['./grouped-work-packages.component.css'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ThreadSafeService], // Provide the service here
})
export class GroupedWorkPackagesComponent implements OnInit {
  @Input() data: DateGroupedWorkPackages;
  @Input() users: User[];
  @Input() accountingYear: any;
  @Input() userOptions: any[];
  @Input() event: IEvent;
  @Input() viewMode: EventViewMode;
  @Output() componentChanged = new EventEmitter<IComponentChanged>();
  @Output() componentLoaded = new EventEmitter<boolean>();
  @Output() doReload = new EventEmitter();

  public enumViewMode = EventViewMode;
  public date: Date;
  public dayOfWeek: string;
  public viewData: DateGroupedWorkPackages;
  public groupedWps: any[] = [];
  public showOverall: boolean = true;
  public newDate: Date = new Date();
  private userData: User;
  private routeSub: Subscription;
  private debugModeOn: boolean = false;
  public dayName: string = "";

  constructor(
    private common: CommonFunctionsService,
    public dialog: MatDialog,
    private route: ActivatedRoute
  ) {
    // Assign the data to the data source for the table to render
    this.common.registerEventEmitterHandlerUserData(async (data: User) => {
      this.userData = data;
    });
  }

  //Info: Das Init wird nur ein mal gecalled, aber die Methode update() wird mehrfach gecalled.
  //Das liegt daran, dass es fuer jedes Datum ein neues DateGroupedWorkPackages-Objekt gibt.
  //Das laesst sich nicht so richtig mit dem ThreadSafeService verbinden, da der component state immer ueberschrieben wird.
  //Liegt eher am schlechten Design der Komponente. Da aber 'fast' alle Daten der Komponente bereits vorher geladen wurden,
  //ist das ok so.

  ngOnInit(): void {
    //enable debug mode if set in url param
    this.routeSub = this.route.queryParams.subscribe((params) => {
      this.debugModeOn = params['debug'] === 'true';
    });
  }

  
  ngAfterContentInit() {
    if (this.data) {
      // this.update();
      this.viewData = this.data;
      //sort by type, group related shift names together (e.g. "Mix Cocktails I/II/III")
      this.groupedWps = _.values(_.groupBy(this.viewData.workPackages, 'type'));

      const baseName = (name: string) => name.replace(/\s+(I{1,3}|IV|V|VI{0,3}|IX|X|\d+)\s*$/, '').trim();
      const baseGroupMinStart: { [key: string]: number } = {};
      this.groupedWps.forEach(group => {
        const base = baseName(group[0]?.type || '');
        const startSec = group[0]?.start?.seconds ?? 0;
        if (baseGroupMinStart[base] === undefined || startSec < baseGroupMinStart[base]) {
          baseGroupMinStart[base] = startSec;
        }
      });
      this.groupedWps.sort((a, b) => {
        const baseA = baseName(a[0]?.type || '');
        const baseB = baseName(b[0]?.type || '');
        const groupDiff = (baseGroupMinStart[baseA] ?? 0) - (baseGroupMinStart[baseB] ?? 0);
        if (groupDiff !== 0) return groupDiff;
        if (baseA !== baseB) return baseA < baseB ? -1 : 1;
        return (a[0]?.start?.seconds ?? 0) - (b[0]?.start?.seconds ?? 0);
      });

      this.decideIfAllShiftsFull(this.data.workPackages);
  
      // console.log('(GWP) Emit loaded for group: ' + this.data.date.toDateString());
      this.componentLoaded.emit(true);
    }
  }

  ngAfterViewInit() {
  }

  //Decide if a shift shall be shown
  public showWp(wp: IWorkPackage): boolean {
    //at this point in time, no work packages are loaded. Return true to allow loading and then decide if to hide
    if (wp && wp.participants == undefined) {
      return true;
    }
    return this.common.showWpView(wp, this.viewMode, this.userData);
  }

  //If all shifts decide to hide (for whatever reason), set this grouped work package as full
  private decideIfAllShiftsFull(wps: IWorkPackage[]) {
    //if all work packages decide to hide, hide the whole group
    var all = wps.every(
      (wp) => !this.common.showWpView(wp, this.viewMode, this.userData)
    );
    if (all) {
      // if (this.debugModeOn)
      //   console.log(
      //     '(GWP) Group will not show'
      //   );
      this.showOverall = false;
    } else {
      // if (this.debugModeOn)
      //   console.log('(GWP) Group can be shown');
      this.showOverall = true;
    }
    return this.showOverall;
  }

  public getDayName(date: Date) {
    this.dayName= this.common.getDayName(date);
  }

  public wpChanged() {
    // this.update();
    //propagate to upward component
    this.componentChanged?.emit();
  }

  //To avoid: Expression has changed after it was checked
  public getDate(): Date {
    if (this.viewData) return this.viewData.date;
    return new Date();
  }

  //Called when sub compoenent is loaded
  public wpViewLoaded(workPackage: IWorkPackage) {
    // console.log('(GWP): sub comp. wpView loaded: '+ workPackage.id);
  }

  //show debug info about a single work package
  public showWpDebugInfo(wp: IWorkPackage) {
    if (!this.debugModeOn) {
      return '';
    }
    return this.common.showWpDebugInfo(wp, this.userData, this.viewMode);
  }

  //Show debug info about the grouped work package
  public showGWpDebugInfo() {
    if (!this.debugModeOn) {
      return '';
    }
    return `Decision to show overall: ${this.showOverall}`;
  }

  public isDebugMode(): boolean {
    return this.debugModeOn;
  }

  public trackByWpGroup(index: number, wps: IWorkPackage[]): string {
    return wps.map(wp => wp.id).join(',');
  }

  public trackByWpId(index: number, wp: IWorkPackage): string {
    return wp.id;
  }
}
